/**
 * アートワーク型ポスター
 *
 * 主役の写真をアクセント色の 2 色刷りに落とした背景の上に、中央揃えでコピーと行程を載せる。
 */

import { shortPrefectureName } from '@/lib/prefectures'

import { dateRangeLabel, durationLabel } from '../trip-utils'
import type { PosterInput } from './poster-canvas'
import {
  BG,
  drawDuotone,
  drawRoute,
  drawScrim,
  drawStats,
  fitLines,
  FONT,
  HEIGHT,
  MARGIN,
  statItems,
  SUBTLE,
  TEXT,
  WIDTH,
  withAlpha,
} from './poster-canvas'

export const renderArtworkPoster = (ctx: CanvasRenderingContext2D, input: PosterInput) => {
  const { trip, accent, hero, headline, subcopy, keywords, dayLines, routePoints } = input

  ctx.fillStyle = BG
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  if (hero) drawDuotone(ctx, hero, accent)

  drawScrim(ctx, 0, 300, 0.6, 0)
  drawScrim(ctx, 620, HEIGHT, 0, 0.94)

  ctx.textAlign = 'center'
  const centerX = WIDTH / 2

  // ── 上部：期間・タイトル ───────────────────
  ctx.fillStyle = accent
  ctx.font = FONT(26, 700)
  ctx.fillText(`${durationLabel(trip)}\u3000${keywords.slice(0, 3).join('\u3000')}`, centerX, MARGIN + 44)

  ctx.fillStyle = TEXT
  ctx.font = FONT(30, 700)
  ctx.fillText(trip.title, centerX, MARGIN + 92)

  // ── 中央：コピー ─────────────────────────
  const maxWidth = WIDTH - MARGIN * 2
  const { size, lines } = fitLines(ctx, headline, maxWidth, 2, 96, 52)
  let baseline = 800
  for (const line of lines) {
    ctx.fillStyle = TEXT
    ctx.font = FONT(size, 700)
    ctx.fillText(line, centerX, baseline)
    baseline += size * 1.18
  }

  baseline += 12
  ctx.fillStyle = SUBTLE
  ctx.font = FONT(28)
  ctx.fillText(subcopy, centerX, baseline)

  // ── 罫線 ────────────────────────────────
  baseline += 44
  ctx.strokeStyle = withAlpha(accent, 0.8)
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(centerX - 120, baseline)
  ctx.lineTo(centerX + 120, baseline)
  ctx.stroke()

  // ── 行程 ────────────────────────────────
  baseline += 48
  ctx.font = FONT(26)
  for (const line of dayLines.slice(0, 5)) {
    ctx.fillStyle = TEXT
    ctx.fillText(line, centerX, baseline)
    baseline += 40
  }

  ctx.fillStyle = SUBTLE
  ctx.font = FONT(24)
  const prefLabel = trip.stats.prefectures.map(shortPrefectureName).join(' · ')
  ctx.fillText(prefLabel || dateRangeLabel(trip), centerX, baseline + 12)

  ctx.textAlign = 'left'
  drawRoute(ctx, routePoints, { x: WIDTH - MARGIN - 200, y: MARGIN + 20, w: 200, h: 200 }, accent)
  drawStats(ctx, statItems(trip), HEIGHT - MARGIN - 96)
}
